/**
 * Billing approvals: the second pair of eyes (decision D7).
 *
 * Credits above KES 5,000, every refund, long trial extensions and free
 * service days wait here until someone other than the requester decides.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { Button, PageHeader } from '@lipaflow/ui'
import { useAuthStore, apiErrorMessage } from '@lipaflow/shared'
import { billingApprovals, type ApprovalKind } from '../services/billingApprovals'
import { usePageTitle } from '../lib/pageTitle'
import { useSector } from '../sector/sector'

const money = (n: number) => `KES ${Math.round(n).toLocaleString('en-KE')}`
const EXTENSION_LABEL = { trial: 'Longer trial', due_date: 'Later due date', grace: 'Longer grace', service: 'Free service days' }

interface Row {
  kind: ApprovalKind
  id: string
  tenant: string
  what: string
  reason: string
  requestedBy: string | null
}

export default function BillingApprovalsPage() {
  usePageTitle('Billing approvals')
  const queryClient = useQueryClient()
  const me = useAuthStore((s) => s.user?.id)
  const sector = useSector()
  const { data, isLoading } = useQuery({
    queryKey: ['billing-approvals', sector.code],
    queryFn: () => billingApprovals.list(sector.code),
  })

  const decide = useMutation({
    mutationFn: ({ kind, id, approve }: { kind: ApprovalKind; id: string; approve: boolean }) =>
      billingApprovals.decide(kind, id, approve),
    onSuccess: (_result, { approve }) => {
      toast.success(approve ? 'Approved' : 'Rejected')
      queryClient.invalidateQueries({ queryKey: ['billing-approvals'] })
    },
    onError: (err: unknown) =>
      toast.error(apiErrorMessage(err, "Couldn't decide")),
  })

  const rows: Row[] = [
    ...(data?.credits ?? []).map((c) => ({
      kind: 'credits' as const, id: c.id, tenant: c.tenant_name ?? c.tenant_id,
      what: `Credit ${money(c.amount)} (${c.source})`, reason: c.reason, requestedBy: c.requested_by,
    })),
    ...(data?.refunds ?? []).map((r) => ({
      kind: 'refunds' as const, id: r.id, tenant: r.tenant_name ?? r.tenant_id,
      what: `Cash refund ${money(r.amount)}`, reason: r.reason, requestedBy: r.requested_by,
    })),
    ...(data?.extensions ?? []).map((e) => ({
      kind: 'extensions' as const, id: e.id, tenant: e.tenant_name ?? e.tenant_id,
      what: `${EXTENSION_LABEL[e.kind]}: +${e.days} days`, reason: e.reason, requestedBy: e.requested_by,
    })),
  ]

  return (
    <div className="space-y-4">
      <PageHeader
        title="Billing approvals"
        subtitle="Credits over KES 5,000, refunds, long trial extensions and free service days need a second person."
      />
      {isLoading && <p className="text-body-sm text-ink-muted">Loading…</p>}
      {!isLoading && rows.length === 0 && (
        <p className="rounded-card border border-hairline bg-surface p-6 text-body-sm text-ink-muted">Nothing is waiting.</p>
      )}
      <ul className="space-y-3">
        {rows.map((row) => {
          const own = row.requestedBy !== null && row.requestedBy === me
          return (
            <li key={`${row.kind}-${row.id}`} className="rounded-card border border-hairline bg-surface p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-body font-medium text-ink">{row.what}</p>
                  <p className="text-body-sm text-ink-muted">{row.tenant}</p>
                  <p className="mt-1 text-body-sm text-ink">{row.reason}</p>
                </div>
                {own ? (
                  <p className="text-caption text-ink-muted">You asked for this; someone else must decide.</p>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      disabled={decide.isPending}
                      onClick={() => decide.mutate({ kind: row.kind, id: row.id, approve: false })}
                    >
                      Reject
                    </Button>
                    <Button
                      disabled={decide.isPending}
                      onClick={() => decide.mutate({ kind: row.kind, id: row.id, approve: true })}
                    >
                      Approve
                    </Button>
                  </div>
                )}
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
